import * as React from 'react'

/**
 * rc-util's `useMergedState`, reduced to what the Select seam needs.
 *
 * The part that MUST be copied — and the reason this is not a hand-rolled
 * `props.value ?? internal` — is the layout effect: when a controlled prop
 * goes back to `undefined`, the inner value has to be reset with it. Without
 * that, a controlled parent that clears its own state (`value={undefined}`)
 * keeps seeing the seam's stale inner value, so the control still paints the
 * cleared selection AND the next pick of that same option is judged
 * "unchanged" and never fires `onChange`.
 *
 * Mirrors `node_modules/rc-util/es/hooks/useMergedState.js`, the version antd
 * 5.15.4 ships. `undefined` is the only value treated as empty; `null` is a
 * real, controlled value — call sites here pass `value={null}` to mean
 * "nothing selected".
 */
export function useMergedState<T>(
  defaultStateValue: T,
  option?: { value?: T; defaultValue?: T }
): [T, (next: T) => void] {
  const { value, defaultValue } = option || {}

  const [innerValue, setInnerValue] = React.useState<T>(() => {
    if (value !== undefined) return value
    if (defaultValue !== undefined) return defaultValue
    return defaultStateValue
  })

  const merged = value !== undefined ? value : innerValue

  const isFirstRender = React.useRef(true)
  React.useLayoutEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }
    if (value === undefined) setInnerValue(value as T)
  }, [value])

  return [merged, setInnerValue]
}
